import React, { useState } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, CircularProgress, Box, Typography } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler} from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import httpclient from "../../../../Utils";

const ReassignOrderButton = ({
  jobId,
  currentDriverName,
  currentDriverId, // Add this new prop
  allDrivers = [],
  onSuccess,
  buttonText = "Reassign Invoice",
  variant = "outlined",
  size = "medium",
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();
console.log("Current Driver ID:", currentDriverId);
  // Filter available drivers (exclude current driver by driver_id)
  const availableDrivers = allDrivers.filter(
    (driver) => driver.driver_id !== currentDriverId
  );

  const handleOpen = () => {
    setOpenDialog(true);
    setSelectedDriverId("");
    setConfirmOpen(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedDriverId("");
    setConfirmOpen(false);
  };

  const handleNext = () => {
    if (!selectedDriverId) {
      showNotification("warning", "Please select a driver");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    const API_URL = `delivery/orders/invoice/reassign/${jobId}`;

    try {
      const { data } = await httpclient.post(API_URL, {
        driver_id: selectedDriverId,
      });

      if (data.status === 200) {
        showNotification("success", data.message || "Order reassigned successfully");
        handleClose();
        if (onSuccess && typeof onSuccess === "function") {
          onSuccess();
        }
      } else {
        showNotification("info", data.message || "Failed to reassign order");
        setConfirmOpen(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else if (error.response?.status === 404) {
        showNotification("error", "Order or driver not found");
      } else {
        handleApiError(error);
      }
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDriver = availableDrivers.find(
    (d) => d.driver_id === parseInt(selectedDriverId)
  );

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpen}
        startIcon={<SwapHorizIcon />}
        sx={{
          borderColor: "#7152F3",
          color: "#7152F3",
          fontWeight: 600,
          px: 3,
          py: 1,
          borderRadius: "8px",
          textTransform: "none",
          fontSize: "14px",
          "&:hover": {
            borderColor: "#5c34fa",
            backgroundColor: "rgba(113, 82, 243, 0.08)",
          },
        }}
      >
        {buttonText}
      </Button>

      {/* Driver Selection Dialog */}
      <Dialog
        open={openDialog}
        onClose={!submitting ? handleClose : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Reassign Order
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Reassign order <strong>{jobId}</strong> from{" "}
              <strong>{currentDriverName}</strong> to a different driver:
            </Typography>

            <Box
              sx={{
                bgcolor: "#f5f5f5",
                p: 1.5,
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Current Driver:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {currentDriverName}
              </Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Select Driver</InputLabel>
              <Select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                label="Select Driver"
                disabled={submitting}
              >
                {availableDrivers.length === 0 ? (
                  <MenuItem disabled>No other drivers available</MenuItem>
                ) : (
                  availableDrivers.map((driver) => (
                    <MenuItem key={driver.driver_id} value={driver.driver_id}>
                      {driver.full_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={submitting}
            sx={{
              borderColor: "#7152F3",
              color: "#7152F3",
              "&:hover": {
                borderColor: "#5c34fa",
                backgroundColor: "rgba(113, 82, 243, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!selectedDriverId || submitting}
            sx={{
              backgroundColor: "#7152F3",
              "&:hover": {
                backgroundColor: "#5c34fa",
              },
            }}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={!submitting ? () => setConfirmOpen(false) : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Reassignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reassign order <strong>{jobId}</strong> from{" "}
            <strong>{currentDriverName}</strong> to{" "}
            <strong>{selectedDriver?.full_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            disabled={submitting}
            sx={{
              borderColor: "#7152F3",
              color: "#7152F3",
              "&:hover": {
                borderColor: "#5c34fa",
                backgroundColor: "rgba(113, 82, 243, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: "#7152F3",
              "&:hover": {
                backgroundColor: "#5c34fa",
              },
            }}
          >
            {submitting ? <CircularProgress size={24} /> : "Confirm Reassign"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReassignOrderButton;